// src/components/admin/RoleManagement.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, Modal,
  Spinner, Alert, Badge, InputGroup
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaUserShield, FaSave, FaTimes,
  FaUniversity, FaBuilding, FaSearch, FaArrowLeft
} from 'react-icons/fa';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal & Form states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);

  const [formData, setFormData] = useState({
    role_name: '',
    description: ''
  });

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = !user.college_id;
  const isCollegeLevel = !!selectedCollege?.college_id;

  useEffect(() => {
    // Check if user has college_id (college admin/staff)
    if (user?.college_id) {
      setSelectedCollege({ college_id: user.college_id, college_name: 'My College' });
    } else {
      // System-level admin
      fetchColleges();
    }
  }, []);

  useEffect(() => {
    if (selectedCollege) {
      if (selectedCollege.college_id) {
        fetchCollegeRoles(selectedCollege.college_id);
      } else {
        fetchSystemRoles();
      }
    }
  }, [selectedCollege]);

  // Fetch all colleges (for super admin)
  const fetchColleges = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/api/college');
      if (response.success && Array.isArray(response.message?.showCollege)) {
        setColleges(response.message.showCollege);
      }
    } catch (err) {
      setError('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };

  // Fetch college-specific roles + staff details
  const fetchCollegeRoles = async (collegeId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(`/api/user-admin/staff/${collegeId}`);

      if (response.success && Array.isArray(response.message?.roles)) {
        setRoles(response.message.roles);
      } else {
        setError('Failed to load college roles');
      }
    } catch (err) {
      setError('Error loading college roles: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch system/global roles
  const fetchSystemRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/api/roles');

      if (response.success && Array.isArray(response.message?.roles)) {
        const systemRoles = response.message.roles.filter(role => role.college_id === null);
        setRoles(systemRoles);
      } else {
        setError('Failed to load system roles');
      }
    } catch (err) {
      setError('Error loading system roles: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Refresh roles after create/update/delete
  const refreshRoles = () => {
    if (selectedCollege?.college_id) {
      fetchCollegeRoles(selectedCollege.college_id);
    } else {
      fetchSystemRoles();
    }
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Open modal for create / edit
  const openModal = (role = null) => {
    if (role) {
      setIsEditMode(true);
      setCurrentRoleId(role.role_id);
      setFormData({
        role_name: role.role_name || '',
        description: role.description || ''
      });
    } else {
      setIsEditMode(false);
      setCurrentRoleId(null);
      setFormData({
        role_name: '',
        description: ''
      });
    }
    setShowModal(true);
  };

  // Save (Create or Update)
  const handleSave = async () => {
    if (!formData.role_name.trim()) {
      setError('Role name is required');
      return;
    }

    try {
      setError(null);
      setSuccessMsg('');

      const payload = {
        role_name: formData.role_name.trim(),
        description: formData.description.trim()
      };

      // For college-level, we need to include college_id
      if (selectedCollege?.college_id) {
        payload.college_id = selectedCollege.college_id;
      }

      let response;
      if (isEditMode) {
        response = await apiService.put(`/api/roles/${currentRoleId}`, payload);
      } else {
        response = await apiService.post('/api/roles', payload);
      }

      if (response.success) {
        setSuccessMsg(isEditMode ? 'Role updated successfully' : 'Role created successfully');
        setShowModal(false);
        refreshRoles();
      } else {
        setError(response.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    }
  };

  // Delete role
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete role "${name}"?`)) return;

    try {
      const response = await apiService.delete(`/api/roles/${id}`);
      if (response.success) {
        setSuccessMsg('Role deleted successfully');
        refreshRoles();
      } else {
        setError('Failed to delete role');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting role');
    }
  };

  // Filter colleges
  const filteredColleges = colleges.filter(college =>
    college.college_name?.toLowerCase().includes(collegeSearch.toLowerCase()) ||
    college.college_code?.toLowerCase().includes(collegeSearch.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <h2 className="mb-4">
        <FaUserShield className="me-2 text-primary" />
        Role Management
      </h2>

      {/* College Selection View (Super Admin only) */}
      {isSuperAdmin && !selectedCollege && (
        <>
          <InputGroup className="mb-4">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search college by name or code..."
              value={collegeSearch}
              onChange={(e) => setCollegeSearch(e.target.value)}
            />
          </InputGroup>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Row>
              {/* Non-College Tile */}
              <Col md={4} lg={3} className="mb-4">
                <Card
                  className="shadow-sm h-100 text-center cursor-pointer"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCollege({ college_id: null, college_name: 'System Roles' })}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUserShield size={50} className="text-primary mb-3 mx-auto" />
                    <h5>System Roles</h5>
                    <p className="text-muted small">Global / System-level roles</p>
                  </Card.Body>
                </Card>
              </Col>

              {/* College Tiles */}
              {filteredColleges.map(college => (
                <Col md={4} lg={3} key={college.college_id} className="mb-4">
                  <Card
                    className="shadow-sm h-100 text-center cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCollege(college)}
                  >
                    <Card.Body className="d-flex flex-column justify-content-center">
                      <FaUniversity size={50} className="text-primary mb-3 mx-auto" />
                      <h5>{college.college_name}</h5>
                      {college.college_code && (
                        <Badge bg="info" className="mx-auto">{college.college_code}</Badge>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      {/* Role List View */}
      {selectedCollege && (
        <>
      <Row className="mb-4 align-items-center">
        <Col>
          {isSuperAdmin && (
            <Button
              variant="outline-secondary"
              className="mb-2"
              onClick={() => {
                setSelectedCollege(null);
                setRoles([]);
                setCollegeSearch('');
              }}
            >
              <FaArrowLeft className="me-2" /> Back to College List
            </Button>
          )}
          <h4 className="mb-0">
            Roles for <strong>{selectedCollege.college_name || 'System'}</strong>
          </h4>
          <p className="text-muted">
            {isCollegeLevel 
              ? 'Manage roles specific to your college' 
              : 'Manage global/system-level roles'}
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => openModal()}>
            <FaPlus className="me-2" /> Create New Role
          </Button>
        </Col>
      </Row>

      {/* Messages */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Loading / Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading roles...</p>
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Role Name</th>
                  <th>Description</th>
                  {isCollegeLevel && <th>College</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={isCollegeLevel ? 5 : 4} className="text-center py-5 text-muted">
                      No roles found. Create your first role!
                    </td>
                  </tr>
                ) : (
                  roles.map((role, index) => (
                    <tr key={role.role_id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{role.role_name}</strong>
                        {role.college_id === null && (
                          <Badge bg="danger" className="ms-2">System</Badge>
                        )}
                      </td>
                      <td>{role.description || <span className="text-muted">—</span>}</td>
                      {isCollegeLevel && (
                        <td>
                          <Badge bg="info">
                            <FaBuilding className="me-1" /> College
                          </Badge>
                        </td>
                      )}
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openModal(role)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(role.role_id, role.role_name)}
                          disabled={role.role_name === 'Admin' || role.role_name === 'Erish Admin'}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
      </>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit Role' : 'Create New Role'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label>Role Name *</Form.Label>
              <Form.Control
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
                placeholder="e.g. Teacher, Student, Moderator"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description about this role's responsibilities..."
              />
            </Form.Group>

            {isCollegeLevel && (
              <Form.Text className="text-muted">
                This role will be created for your college (ID: {selectedCollege?.college_id})
              </Form.Text>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update Role' : 'Create Role'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RoleManagement;